import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import LocationSelector from './components/LocationSelector';
import LocationInventoryView from './components/LocationInventoryView';
import LocationPOSInterface from './components/LocationPOSInterface';
import InterLocationTransfer from './components/InterLocationTransfer';
import LocationAnalytics from './components/LocationAnalytics';
import { posService } from '../../lib/posService';
import { gymService } from '../../lib/services';

const LocationBasedPOSInventory = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('inventory');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      loadLocationData();
    }
  }, [selectedLocation]);

  const loadLocations = async () => {
    setLoading(true);
    const { data } = await gymService?.getActiveGyms();
    if (data && data?.length > 0) {
      setLocations(data);
      // Set default location based on user's gym or first available
      const defaultLocation = userProfile?.gym_id 
        ? data?.find(g => g?.id === userProfile?.gym_id) 
        : data?.[0];
      setSelectedLocation(defaultLocation?.id);
    }
    setLoading(false);
  };

  const loadLocationData = async () => {
    setLoading(true);
    await Promise.all([
      loadProducts(),
      loadCategories(),
      checkLowStockAlerts()
    ]);
    setLoading(false);
  };

  const loadProducts = async () => {
    const { data } = await posService?.getProducts(selectedLocation);
    if (data) {
      setProducts(data);
    }
  };

  const loadCategories = async () => {
    const { data } = await posService?.getCategories(selectedLocation);
    if (data) {
      setCategories(data);
    }
  };

  const checkLowStockAlerts = async () => {
    const { data } = await posService?.getProducts(selectedLocation);
    if (data) {
      const alerts = data?.filter(p => p?.stock_quantity <= p?.low_stock_threshold);
      setLowStockAlerts(alerts);
    }
  };

  const handleLocationChange = (locationId) => {
    setSelectedLocation(locationId);
    setCart([]); // Clear cart when switching locations
  };

  const handleAddToCart = (product) => {
    setCart(prev => {
      const existing = prev?.find(item => item?.id === product?.id);
      if (existing) {
        return prev?.map(item =>
          item?.id === product?.id
            ? { ...item, quantity: item?.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const handleUpdateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      setCart(prev => prev?.filter(item => item?.id !== productId));
    } else {
      setCart(prev => prev?.map(item =>
        item?.id === productId ? { ...item, quantity } : item
      ));
    }
  };

  const handleRemoveFromCart = (productId) => {
    setCart(prev => prev?.filter(item => item?.id !== productId));
  };

  const handleClearCart = () => {
    setCart([]);
  };

  const handleCheckout = async (paymentData) => {
    const items = cart?.map(item => ({
      product_id: item?.id,
      product_name: item?.name,
      quantity: item?.quantity,
      unit_price: item?.price,
      subtotal: item?.price * item?.quantity
    }));

    const { data, error } = await posService?.createTransaction(
      selectedLocation,
      paymentData,
      items
    );

    if (!error) {
      setCart([]);
      await loadLocationData(); // Refresh inventory
      return { success: true, data };
    }
    return { success: false, error };
  };

  const tabs = [
    { id: 'inventory', label: 'Inventory', icon: 'Package' },
    { id: 'pos', label: 'Point of Sale', icon: 'ShoppingCart' },
    { id: 'transfer', label: 'Inter-Location Transfer', icon: 'ArrowLeftRight' },
    { id: 'analytics', label: 'Location Analytics', icon: 'BarChart3' }
  ];

  const currentLocation = locations?.find(l => l?.id === selectedLocation);

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Location-Based POS & Inventory</h1>
              <p className="text-muted-foreground mt-1">Manage retail and inventory across multiple gym locations</p>
            </div>
          </div>

          {/* Location Selector */}
          <LocationSelector
            locations={locations}
            selectedLocation={selectedLocation}
            onLocationChange={handleLocationChange}
            currentLocation={currentLocation}
          />

          {/* Low Stock Alerts */}
          {lowStockAlerts?.length > 0 && (
            <div className="mt-4 bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-warning mb-1">Low Stock Alert</h3>
                  <p className="text-xs text-muted-foreground">
                    {lowStockAlerts?.length} product{lowStockAlerts?.length > 1 ? 's' : ''} at {currentLocation?.name} below threshold
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setActiveTab('inventory')}
                >
                  View Details
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-1">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`px-4 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div>
            {activeTab === 'inventory' && (
              <LocationInventoryView
                locationId={selectedLocation}
                products={products}
                categories={categories}
                lowStockAlerts={lowStockAlerts}
                onRefresh={loadLocationData}
              />
            )}

            {activeTab === 'pos' && (
              <LocationPOSInterface
                locationId={selectedLocation}
                locationName={currentLocation?.name}
                products={products}
                categories={categories}
                cart={cart}
                onAddToCart={handleAddToCart}
                onUpdateQuantity={handleUpdateCartQuantity}
                onRemoveFromCart={handleRemoveFromCart}
                onClearCart={handleClearCart}
                onCheckout={handleCheckout}
              />
            )}

            {activeTab === 'transfer' && (
              <InterLocationTransfer
                sourceLocationId={selectedLocation}
                locations={locations}
                products={products}
                onTransferComplete={loadLocationData}
              />
            )}

            {activeTab === 'analytics' && (
              <LocationAnalytics
                locationId={selectedLocation}
                locationName={currentLocation?.name}
                products={products}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationBasedPOSInventory;